// frontend/src/workshop/components/shapes/memory-frame/mock-data.ts

import { GRAPH_NODE_COLORS } from './types';
import type { ForceGraphData, ForceGraphNode, GraphNodeType } from './types';

function n(
  id: string,
  label: string,
  type: GraphNodeType,
  val = 4,
  description?: string
): ForceGraphNode {
  return { id, name: label, label, type, color: GRAPH_NODE_COLORS[type], val, description };
}

function l(source: string, target: string, type: string, label?: string, value = 1) {
  return { source, target, type, label, value };
}

// ─── Small graph: 8 nodes, 7 links ────────────────────────────────────────────
export const SMALL_GRAPH: ForceGraphData = {
  nodes: [
    n('you',        'You',           'user_profile', 8),
    n('work',       'Work',          'category',     5),
    n('learning',   'Learning',      'category',     5),
    n('alice',      'Alice Chen',    'person',       4,  'Engineering lead'),
    n('denker',     'Denker AI',     'project',      4,  'The canvas workspace'),
    n('react',      'React Hooks',   'concept',      4),
    n('shipped',    'Shipped MVP',   'outcome',      4),
    n('typescript', 'TypeScript',    'concept',      3),
  ],
  links: [
    l('you',     'work',       'has_category'),
    l('you',     'learning',   'has_category'),
    l('work',    'alice',      'collaborates_with'),
    l('work',    'denker',     'owns'),
    l('denker',  'shipped',    'produced'),
    l('learning','react',      'studying'),
    l('learning','typescript', 'studying'),
  ],
};

// ─── Medium graph: 18 nodes, 17 links ─────────────────────────────────────────
export const MEDIUM_GRAPH: ForceGraphData = {
  nodes: [
    n('you',        'You',              'user_profile', 8),
    n('work',       'Work',             'category',     5),
    n('personal',   'Personal',         'category',     5),
    n('learning',   'Learning',         'category',     5),
    n('alice',      'Alice Chen',       'person',       4,  'Engineering lead'),
    n('bob',        'Bob Smith',        'person',       4,  'Product designer'),
    n('denker',     'Denker AI',        'project',      4),
    n('blog',       'Personal Blog',    'project',      4),
    n('react',      'React Hooks',      'concept',      4),
    n('typescript', 'TypeScript',       'concept',      3),
    n('figma',      'Figma',            'resource',     3),
    n('mvp',        'Shipped MVP',      'outcome',      4),
    n('berlin',     'Berlin Office',    'company',      3),
    n('workflow',   'Daily Standup',    'process',      3),
    n('pref-async', 'Async first',      'preference',   3),
    n('notes',      'Meeting Notes',    'document',     3),
    n('design-sys', 'Design System',    'project',      4),
    n('growth',     'User Growth +40%', 'outcome',      4),
  ],
  links: [
    l('you',        'work',       'has_category'),
    l('you',        'personal',   'has_category'),
    l('you',        'learning',   'has_category'),
    l('work',       'alice',      'collaborates_with'),
    l('work',       'bob',        'collaborates_with'),
    l('work',       'denker',     'owns'),
    l('work',       'berlin',     'works_at'),
    l('work',       'workflow',   'follows'),
    l('denker',     'mvp',        'produced'),
    l('denker',     'design-sys', 'uses'),
    l('denker',     'growth',     'produced'),
    l('personal',   'blog',       'owns'),
    l('personal',   'pref-async', 'has_preference'),
    l('learning',   'react',      'studying'),
    l('learning',   'typescript', 'studying'),
    l('alice',      'notes',      'created'),
    l('bob',        'figma',      'uses'),
  ],
};

// ─── Large graph: 34 nodes, 31 links ──────────────────────────────────────────
export const LARGE_GRAPH: ForceGraphData = {
  nodes: [
    n('you',         'You',               'user_profile', 8),
    n('work',        'Work',              'category',     5),
    n('personal',    'Personal',          'category',     5),
    n('learning',    'Learning',          'category',     5),
    n('network',     'Network',           'category',     5),
    n('alice',       'Alice Chen',        'person',       4,  'Engineering lead'),
    n('bob',         'Bob Smith',         'person',       4,  'Product designer'),
    n('clara',       'Clara Müller',      'person',       4,  'Backend engineer'),
    n('david',       'David Park',        'person',       3,  'Investor'),
    n('denker',      'Denker AI',         'project',      4),
    n('blog',        'Personal Blog',     'project',      3),
    n('design-sys',  'Design System',     'project',      4),
    n('api-v2',      'API v2',            'project',      3),
    n('react',       'React Hooks',       'concept',      4),
    n('typescript',  'TypeScript',        'concept',      3),
    n('pgvector',    'pgvector',          'concept',      3),
    n('ddd',         'Domain Design',     'concept',      3),
    n('figma',       'Figma',             'resource',     3),
    n('cursor',      'Cursor AI',         'resource',     3),
    n('linear',      'Linear',            'resource',     3),
    n('mvp',         'Shipped MVP',       'outcome',      4),
    n('growth',      'User Growth +40%',  'outcome',      4),
    n('perf',        'Perf win 2×',       'outcome',      3),
    n('berlin',      'Denker GmbH',       'company',      3),
    n('vc',          'Seed Round €2M',    'company',      3),
    n('standup',     'Daily Standup',     'process',      3),
    n('deploy',      'CI/CD Pipeline',    'process',      3),
    n('pref-async',  'Async first',       'preference',   3),
    n('pref-docs',   'Docs before code',  'preference',   3),
    n('notes',       'Meeting Notes',     'document',     3),
    n('arch-doc',    'Architecture Doc',  'document',     3),
    n('canvas-spec', 'Canvas Spec v2',    'document',     3),
    n('chunk-1',     'React perf notes',  'chunk',        2),
    n('chunk-2',     'DB schema notes',   'chunk',        2),
  ],
  links: [
    l('you',        'work',        'has_category'),
    l('you',        'personal',    'has_category'),
    l('you',        'learning',    'has_category'),
    l('you',        'network',     'has_category'),
    l('work',       'alice',       'collaborates_with'),
    l('work',       'bob',         'collaborates_with'),
    l('work',       'clara',       'collaborates_with'),
    l('work',       'denker',      'owns'),
    l('work',       'berlin',      'works_at'),
    l('work',       'standup',     'follows'),
    l('work',       'deploy',      'uses'),
    l('denker',     'mvp',         'produced'),
    l('denker',     'growth',      'produced'),
    l('denker',     'design-sys',  'contains'),
    l('denker',     'api-v2',      'contains'),
    l('api-v2',     'pgvector',    'uses'),
    l('api-v2',     'perf',        'produced'),
    l('personal',   'blog',        'owns'),
    l('personal',   'pref-async',  'has_preference'),
    l('personal',   'pref-docs',   'has_preference'),
    l('network',    'david',       'knows'),
    l('network',    'vc',          'related_to'),
    l('learning',   'react',       'studying'),
    l('learning',   'typescript',  'studying'),
    l('learning',   'pgvector',    'studying'),
    l('learning',   'ddd',         'studying'),
    l('alice',      'notes',       'created'),
    l('alice',      'arch-doc',    'created'),
    l('bob',        'figma',       'uses'),
    l('bob',        'canvas-spec', 'created'),
    l('arch-doc',   'chunk-2',     'contains'),
  ],
};

export const GRAPH_SCENARIOS = {
  small: SMALL_GRAPH,
  medium: MEDIUM_GRAPH,
  large: LARGE_GRAPH,
} as const;

export type GraphScenario = keyof typeof GRAPH_SCENARIOS;
